import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  FolderKanban, 
  Users, 
  MessageSquare, 
  FileText, 
  CreditCard,
  Settings,
  LogOut,
  X
} from 'lucide-react';
import { Button } from '../ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_001924de-85a6-4169-ab14-49ef05e58c3d/artifacts/qt88pa3s_checkmate%20by%20memarketgroup_1.png";

export const Sidebar = ({ isOpen, onClose, unreadMessages = 0 }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const adminNavItems = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { to: '/admin/clients', icon: Users, label: 'Clients' },
    { to: '/admin/projects', icon: FolderKanban, label: 'Projects' },
    { to: '/admin/documents', icon: FileText, label: 'Documents' },
  ];

  const clientNavItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { to: '/dashboard/projects', icon: FolderKanban, label: 'My Projects' },
    { to: '/dashboard/documents', icon: FileText, label: 'Documents' },
    { to: '/dashboard/settings', icon: Settings, label: 'Settings' },
  ];

  const navItems = isAdmin ? adminNavItems : clientNavItems;

  const NavItem = ({ to, icon: Icon, label, badge, exact }) => (
    <NavLink
      to={to}
      end={exact}
      onClick={onClose}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
          isActive
            ? 'bg-[#4A90D9]/20 text-[#4A90D9] border-l-2 border-[#4A90D9]'
            : 'text-slate-400 hover:text-white hover:bg-white/5'
        }`
      }
      data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium">{label}</span>
      {badge > 0 && (
        <span className="ml-auto bg-[#EF4444] text-white text-xs px-2 py-0.5 rounded-full">
          {badge}
        </span>
      )}
    </NavLink>
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`sidebar glass ${isOpen ? 'open' : ''} flex flex-col`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <img src={LOGO_URL} alt="Checkmate" className="h-10 w-auto" />
          <button 
            className="lg:hidden text-slate-400 hover:text-white"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#4A90D9] flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              exact={item.exact}
              badge={item.label === 'Messages' ? unreadMessages : 0}
            />
          ))}
        </nav>

        {/* Bottom actions */}
        <div className="p-4 border-t border-white/10 space-y-1">
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-white/5"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign Out
          </Button>
        </div>
      </aside>
    </>
  );
};
